import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SchemaForm from 'shared/components/forms/SchemaForm';
import TestAction from 'shared/components/testAction/TestAction';
import { getButtonText } from 'shared/components/testAction/TestActionFunctions';
import {
  SCHEDULE_TOGGLE_FIELD,
  ConfigTarget,
  actionDescription,
  actionIsSwitchedOn,
  configFor,
  hasScheduleToggle,
  isBlank,
  missingRequiredFields,
} from './actionSetup';
import ScheduleToggle from './ScheduleToggle';
import { DraftReferenceState } from 'shared/api/referenceOptions';
import { SegmentedToggle } from 'shared/components/inputs/SegmentedToggle';
import RawConfigEditor from './RawConfigEditor';
import { actionLabel } from './buildActionNav';
import { configBoxClasses } from 'styles/tailwindClassnames';
import UnsavedSectionPrompt, {
  PendingEdits,
  SectionPrompt,
} from './UnsavedSectionPrompt';
import {
  RAW_VIEW_KEY,
  formatV1,
  rawViewOf,
  v1ToForm,
  withoutRawView,
} from './rawView';
import Alert from 'shared/components/ui/Alert';

export type TestActionProps = {
  // The third argument names the integration the action belongs to. Screens that
  // show more than one account (the flow map) must pass it — without it the test
  // runs against whatever account the parent defaults to.
  // `formData` carries the section's unsaved edits, so a test runs against the
  // credentials on screen.
  onTestAction: (
    actionId: any,
    actionValue: any,
    target?: ConfigTarget,
    formData?: any,
  ) => void;
  loading?: boolean;
  response?: any;
};

type ActionSectionFormProps = {
  target: ConfigTarget;
  action: any;
  formData: any;
  formVersion?: number;
  errors?: Record<string, string>;
  editable?: boolean;
  userIsAdmin?: boolean;
  token?: string;
  onChange: (data: any, fieldId?: string) => void;
  test?: TestActionProps;
  // Create flows have nothing saved yet by definition, so the never-saved
  // banner and the required-field hints would state the obvious on every field.
  showSetupState?: boolean;
  // Per-field "Required — not configured yet." hints only. Off for sections whose
  // fields aren't in use right now (a dormant or exempt flow direction): the
  // hints read as errors the user owes, contradicting the notice above the form.
  // The never-saved banner stays — it is true whatever the direction's state.
  showRequiredHints?: boolean;
  // Replaces the Test Action row — create flows show it disabled with a
  // "save first" tooltip instead of a live button.
  headerActions?: React.ReactNode;
  // Off when the host puts the section's on/off switch in its own header (the
  // creation screen stacks sections under a heading and wants the switch on
  // that line). The switch's EFFECTS — read-only fields, no required hints —
  // apply either way; this only says who draws it.
  renderScheduleToggle?: boolean;
  // Create flows pass this so `gundi:reference` fields can fetch live options
  // against the credentials the user just typed, before the integration is saved.
  draftReference?: DraftReferenceState;
  // Read at click time. useConfigEditor stashes edits in a ref and re-renders
  // only on the first one, so the `formData` prop trails the form.
  getFormData?: () => any;
  // This section's own save and discard, for actions with a raw view: leaving
  // the form with unsaved edits asks Save or Discard first, so the raw view can
  // show what is saved. Screens without per-section saving leave it out, and
  // the raw view then shows the values on screen.
  pendingEdits?: PendingEdits;
};

// One action's configuration form: the section body every configuration screen
// renders. Owns the two "this isn't live yet" signals — the never-saved banner and
// the per-field required hints — so they read identically everywhere.
const ActionSectionForm = ({
  target,
  action,
  formData,
  formVersion = 0,
  errors,
  editable = true,
  userIsAdmin = false,
  token,
  onChange,
  test,
  showSetupState = true,
  showRequiredHints = true,
  headerActions,
  renderScheduleToggle = true,
  draftReference,
  getFormData,
  pendingEdits,
}: ActionSectionFormProps) => {
  const { t } = useTranslation();
  const [tested, setTested] = useState(false);
  const saved = configFor(target, action);
  // `formData` can trail the form (useConfigEditor re-renders only on a
  // section's first edit), so flipping the switch builds its change from the
  // latest edits rather than from the last render.
  const onToggle = (data: any, fieldId: string) => {
    const next = {
      ...((getFormData ? getFormData() : formData) ?? {}),
      [SCHEDULE_TOGGLE_FIELD]: !!data[SCHEDULE_TOGGLE_FIELD],
    };
    // After a visit to the raw view the form holds a frozen snapshot
    // (formFromRaw), so the flipped switch has to reach it too or the next
    // field edit would send the old value back.
    if (formFromRaw !== null) setFormFromRaw(next);
    // In the raw view, edits build on rawBase / rawSource(), which don't see the
    // flip, so the next keystroke would send the old value back.
    if (view === 'raw')
      setRawBase({
        ...(rawBase ?? rawSource()),
        [SCHEDULE_TOGGLE_FIELD]: next[SCHEDULE_TOGGLE_FIELD],
      });
    onChange(next, fieldId);
  };
  // A switched-off action is never run by the connector, so its fields are
  // read-only and nothing about them is owed: no required hints, and the
  // screens skip validating it (see actionIsSwitchedOn).
  const switchedOn = actionIsSwitchedOn(action, formData);
  const fieldsEditable = editable && switchedOn;

  // Sections swap this component's props rather than remounting it, so the
  // "a test ran" flag has to follow the action or the previous action's response
  // shows up under the next one. The account counts as much as the action: two
  // accounts of the same technology share action ids, so one account's result
  // would otherwise be displayed under the other's.
  useEffect(() => {
    setTested(false);
  }, [action.id, target.id]);

  // ---- raw view (connector-provided `gundi:raw_view`) ----------------------
  const rawView = rawViewOf(action);
  const [view, setView] = useState<'form' | 'raw'>('form');
  // null until the user types: until then the editor shows rawSource().
  const [rawText, setRawText] = useState<string | null>(null);
  const [rawError, setRawError] = useState<string | null>(null);
  // The form data the raw edits apply on top of, fixed at the first edit.
  const [rawBase, setRawBase] = useState<any>(null);
  // What the form remounts with after leaving the raw view: the formData prop
  // trails the stash (useConfigEditor re-renders only on a section's first edit).
  const [formFromRaw, setFormFromRaw] = useState<any>(null);
  const [prompt, setPrompt] = useState<SectionPrompt | null>(null);

  useEffect(() => {
    setView('form');
    setRawText(null);
    setRawError(null);
    setRawBase(null);
    setFormFromRaw(null);
  }, [action.id, target.id]);
  // A save or a cancel rebases the section on what is stored.
  useEffect(() => {
    setRawText(null);
    setRawError(null);
    setRawBase(null);
    setFormFromRaw(null);
  }, [formVersion]);
  const currentData = () => (getFormData ? getFormData() : formData) ?? {};
  // With per-section saving the raw view shows what is saved once the prompt
  // has settled any edits. Edits made since (typed while a save was in flight)
  // are still pending, so it shows the values on screen, as screens without
  // per-section saving always do.
  const rawSource = () =>
    (pendingEdits && saved && !pendingEdits.hasEdits()
      ? saved.data
      : currentData()) ?? {};

  // A host that draws the on/off switch itself (create screens) changes it
  // through the formData prop, which the snapshot taken on leaving the raw
  // view would otherwise shadow. The snapshot is rebuilt from the latest form
  // data with the host's switch value, so field edits made since the raw visit
  // stay and the next edit doesn't send the old values back.
  const hostSwitch = formData?.[SCHEDULE_TOGGLE_FIELD];
  useEffect(() => {
    setFormFromRaw((prev: any) =>
      prev === null || prev[SCHEDULE_TOGGLE_FIELD] === hostSwitch
        ? prev
        : { ...currentData(), [SCHEDULE_TOGGLE_FIELD]: hostSwitch },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostSwitch]);

  const showRaw = () => {
    setRawText(null);
    setRawError(null);
    setRawBase(null);
    setView('raw');
  };

  const onRawChange = (text: string) => {
    if (!rawView) return;
    const base = rawBase ?? rawSource();
    if (rawBase === null) setRawBase(base);
    setRawText(text);
    const result = v1ToForm(text, rawView, action.schema, base, t);
    if ('error' in result) {
      setRawError(result.error);
      // Unusable text changes nothing: a Save now writes the starting values.
      onChange(base, RAW_VIEW_KEY);
      return;
    }
    setRawError(null);
    onChange(result.data, RAW_VIEW_KEY);
  };

  const onSelectView = (next: string) => {
    if (!rawView) return;
    if (next === 'raw') {
      if (pendingEdits?.hasEdits()) {
        setPrompt({
          onSave: () => pendingEdits.save(),
          onDiscard: () => pendingEdits.discard(),
          proceed: showRaw,
        });
        return;
      }
      showRaw();
      return;
    }
    if (rawText !== null && rawError) return;
    // Unsaved raw edits get the same Save or Discard question as the form's.
    // Either answer rebases the section on what is stored (formVersion), so the
    // form then shows the saved values.
    if (pendingEdits?.hasEdits()) {
      setPrompt({
        onSave: () => pendingEdits.save(),
        onDiscard: () => pendingEdits.discard(),
        proceed: () => {
          setRawText(null);
          setRawBase(null);
          setFormFromRaw(null);
          setView('form');
        },
      });
      return;
    }
    if (rawText !== null) {
      const result = v1ToForm(
        rawText,
        rawView,
        action.schema,
        rawBase ?? rawSource(),
        t,
      );
      if ('error' in result) {
        setRawError(result.error);
        return;
      }
      setFormFromRaw(result.data);
    } else {
      // Nothing typed: the form remounts with what the raw view showed (the
      // saved or still-pending values), not an earlier raw edit's. The form's
      // own snapshot can still hold values a Discard threw away.
      setFormFromRaw(rawSource());
    }
    setView('form');
  };

  // Mark required fields the saved configuration has no value for, the same way
  // validation errors render — that's the "what's missing" pointer the "!" badge
  // promises. A field showing a schema default gets a different sentence: the
  // input isn't empty, so "required" would read as a contradiction, but the value
  // on screen is not the one Gundi holds.
  const properties = action.schema?.properties ?? {};
  const missing =
    showSetupState && showRequiredHints && switchedOn
      ? missingRequiredFields(target, action)
      : [];
  const hints: Record<string, string> = {};
  missing.forEach((f) => {
    if (isBlank(properties[f]?.default))
      hints[f] = t('Required — not configured yet.');
    // Only worth saying once the rest of the action is saved. With nothing saved
    // at all the banner above already says every value on screen is an unsaved
    // default, and repeating that per field adds nothing.
    else if (saved)
      hints[f] = t('Showing the default value — save to apply it.');
  });
  const setupHints = Object.keys(hints).length > 0 ? hints : undefined;
  const togglesHere = renderScheduleToggle && hasScheduleToggle(action);
  // The fields sit in a box headed by the section's name, description, view
  // switch and on/off switch. A host that draws the switch in its own heading
  // already names the section, so it gets no box unless there is a view switch
  // to show.
  const boxed = !!rawView || renderScheduleToggle;
  const descriptionInBox = boxed ? actionDescription(action) : '';
  const scheduleToggle = togglesHere && (
    <ScheduleToggle
      action={action}
      data={formData}
      onChange={onToggle}
      disabled={!editable}
    />
  );

  const rawEditor = rawView && (
    <>
      <p className="mb-3 text-body-sm text-ui-text-primary-text">
        {t(
          'Use this view to copy settings from a Gundi v1 integration. For new integrations, we recommend the Form view.',
        )}
      </p>
      <RawConfigEditor
        value={rawText ?? formatV1(rawSource(), rawView, action.schema)}
        onChange={onRawChange}
        error={rawError}
        readOnly={!fieldsEditable}
      />
    </>
  );
  const schemaForm = (
    <SchemaForm
      key={`${action.id}-v${formVersion}`}
      // Unique per action AND per account: the flow map shows two
      // accounts of the same technology, which share action ids.
      idPrefix={`${target.id ? `${target.id}-` : ''}${action.id}`}
      userIsAdmin={userIsAdmin}
      formTitle=""
      schema={action.schema}
      // The switch above owns run_on_schedule, so the checkbox rjsf would
      // render for it is hidden rather than dropped — omitting a property
      // that `ui:order` lists makes the form fail silently.
      ui_schema={{
        ...(action.ui_schema
          ? {
              ...withoutRawView(action.ui_schema),
              'ui:description': action.ui_schema['ui:description'] || '',
            }
          : {}),
        ...(hasScheduleToggle(action)
          ? {
              [SCHEDULE_TOGGLE_FIELD]: {
                ...(action.ui_schema?.[SCHEDULE_TOGGLE_FIELD] || {}),
                'ui:widget': 'hidden',
              },
            }
          : {}),
      }}
      formdata={formFromRaw ?? formData}
      onChange={(e: any, fieldId?: string) => onChange(e, fieldId)}
      isEditable={fieldsEditable}
      errors={errors ?? setupHints}
      integrationId={target.id}
      connectionName={target.name}
      authToken={token}
      draftReference={draftReference}
      hideRootDescription={!!descriptionInBox}
    />
  );

  // The create screens' stand-in for the test button (see headerActions) sits
  // in the same place as the live one, so both screens lay out alike.
  const testButton = headerActions ? (
    <div className="mb-3">{headerActions}</div>
  ) : (
    action.schema?.is_executable === true &&
    test && (
      <div className="mb-3">
        <TestAction
          actionId={action.id}
          actionValue={action.value}
          onTestAction={(id: any, value: any) => {
            test.onTestAction(
              id,
              value,
              target,
              getFormData ? getFormData() : formData,
            );
            setTested(true);
          }}
          isLoading={!!test.loading && tested}
          response={tested ? test.response : null}
          buttonText={getButtonText(action.value, t)}
          formTitle=""
          uiSchema={action.ui_schema || {}}
        />
      </div>
    )
  );

  return (
    // A titled box is the section's card itself; only the untitled form
    // needs this outer one.
    <div
      className={
        boxed ? '' : 'rounded border border-ui-field-outline bg-white p-4'
      }
    >
      {showSetupState && !saved && (
        <Alert status="warning" className="mb-3">
          {t(
            "This action hasn't been saved yet. The values below are defaults and won't apply until you save.",
          )}
        </Alert>
      )}
      {boxed ? (
        // The switches head a box around the fields they control, so they
        // read as belonging to them rather than to the section above.
        // Named like the section in the side nav, so it reads as that action's.
        <section
          className={configBoxClasses.box}
          aria-label={actionLabel(action, t)}
        >
          <div className={configBoxClasses.header}>
            {/* With switches beside it, half the row at most, so the
                description wraps early and leaves them room to stay on one
                line. Without any, the description takes the whole row. */}
            <div
              className={`min-w-0 ${rawView || scheduleToggle ? 'sm:max-w-[50%]' : 'flex-1'}`}
            >
              <h3 className={configBoxClasses.title}>
                {actionLabel(action, t)}
              </h3>
              {descriptionInBox && (
                <p className="mt-1 text-body-sm text-ui-text-primary-text">
                  {descriptionInBox}
                </p>
              )}
            </div>
            {/* Each control keeps its own label on one line; on a narrow
                screen the On/Off switch wraps under the view switch. */}
            {(rawView || scheduleToggle) && (
              <div className="ml-auto flex flex-wrap items-center justify-end gap-3 whitespace-nowrap">
                {rawView && (
                  <SegmentedToggle
                    value={view}
                    options={[
                      { value: 'form', label: t('Form') },
                      { value: 'raw', label: rawView.title },
                    ]}
                    onChange={onSelectView}
                    ariaLabel={t('Configuration view')}
                  />
                )}
                {scheduleToggle}
              </div>
            )}
          </div>
          <div className={configBoxClasses.body}>
            {testButton}
            {rawView && view === 'raw' ? rawEditor : schemaForm}
          </div>
        </section>
      ) : (
        <>
          {testButton}
          {schemaForm}
        </>
      )}
      <UnsavedSectionPrompt prompt={prompt} onClose={() => setPrompt(null)} />
    </div>
  );
};

export default ActionSectionForm;
